import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  condominiumsApiService,
  CondominiumCreateDTO,
  CondominiumUpdateDTO,
  CondominiumFilters,
} from '../api/condominiums-api';
import { Condominium } from '../types/condominium.types';

interface CondominiumsState {
  condominiums: Condominium[];
  currentCondominium: Condominium | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: CondominiumFilters;
}

const initialState: CondominiumsState = {
  condominiums: [],
  currentCondominium: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
};

// Async Thunks
export const fetchCondominiums = createAsyncThunk(
  'condominiums/fetchCondominiums',
  async (filters: CondominiumFilters = {}, { rejectWithValue }) => {
    try {
      const response = await condominiumsApiService.getCondominiums(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar residenciales'
      );
    }
  }
);

export const fetchCondominium = createAsyncThunk(
  'condominiums/fetchCondominium',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await condominiumsApiService.getCondominiumById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar residencial'
      );
    }
  }
);

export const createCondominium = createAsyncThunk(
  'condominiums/createCondominium',
  async (data: CondominiumCreateDTO, { rejectWithValue }) => {
    try {
      const response = await condominiumsApiService.createCondominium(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear residencial'
      );
    }
  }
);

export const updateCondominium = createAsyncThunk(
  'condominiums/updateCondominium',
  async ({ id, data }: { id: string; data: CondominiumUpdateDTO }, { rejectWithValue }) => {
    try {
      const response = await condominiumsApiService.updateCondominium(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar residencial'
      );
    }
  }
);

export const deleteCondominium = createAsyncThunk(
  'condominiums/deleteCondominium',
  async (id: string, { rejectWithValue }) => {
    try {
      await condominiumsApiService.deleteCondominium(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar residencial'
      );
    }
  }
);

const condominiumsSlice = createSlice({
  name: 'condominiums',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<CondominiumFilters>) => {
      state.filters = action.payload;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
    clearCurrentCondominium: (state) => {
      state.currentCondominium = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchCondominiums.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCondominiums.fulfilled, (state, action) => {
        state.loading = false;
        state.condominiums = action.payload.condominiums;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchCondominiums.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch one
      .addCase(fetchCondominium.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCondominium.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCondominium = action.payload;
      })
      .addCase(fetchCondominium.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createCondominium.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCondominium.fulfilled, (state, action) => {
        state.loading = false;
        state.condominiums.unshift(action.payload);
      })
      .addCase(createCondominium.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateCondominium.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCondominium.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.condominiums.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.condominiums[index] = action.payload;
        }
        if (state.currentCondominium?.id === action.payload.id) {
          state.currentCondominium = action.payload;
        }
      })
      .addCase(updateCondominium.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteCondominium.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCondominium.fulfilled, (state, action) => {
        state.loading = false;
        state.condominiums = state.condominiums.filter((c) => c.id !== action.payload);
        if (state.currentCondominium?.id === action.payload) {
          state.currentCondominium = null;
        }
      })
      .addCase(deleteCondominium.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, setPagination, clearCurrentCondominium, clearError } = condominiumsSlice.actions;

// Selectors
export const selectCondominiums = (state: any) => state.condominiums.condominiums;
export const selectCurrentCondominium = (state: any) => state.condominiums.currentCondominium;
export const selectCondominiumsLoading = (state: any) => state.condominiums.loading;
export const selectCondominiumsError = (state: any) => state.condominiums.error;
export const selectCondominiumsPagination = (state: any) => state.condominiums.pagination;
export const selectCondominiumsFilters = (state: any) => state.condominiums.filters;

export default condominiumsSlice.reducer;

